import Label from 'components/inputs/Label';
import LoadingOverlay from 'components/Overlays/LoadingOverlay';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import { toastText } from 'copy/copy';
import Immutable from 'immutable';
import React, { useState } from 'react';
import { Doc } from 'records/Doc';
import { RespondToSubmittal } from 'records/interactions/RespondToSubmittal';
import { ViewSubmittal } from 'records/interactions/ViewSubmittal';
import { Submittal } from 'records/Submittal';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';
import SubmittalAttachmentButtons from '../SubmittalAttachmentButtons';

interface Props extends WithAppStoreProps {
  interaction: RespondToSubmittal;
}

const RespondToSubmittalOverlay = (props: Props) => {
  const { actions, interaction, appState } = props;
  const [updatingSubmittal, setUpdatingSubmittal] = useState<boolean>(false);
  const [nextSubmittal, setNextSubmittal] = useState<Submittal>(
    props.interaction.submittal
  );

  const { translate } = useTranslator([
    'statusLabels',
    'buttons',
    'submittalsPage',
  ]);

  const handleCloseOverlay = (): void => {
    actions.clearInteractions();
  };

  const handleAddFile = (additionalFiles: FileList): void => {
    handleRemoveClick();

    const files = [...Array.from(additionalFiles)].map((file): File => file);

    const nextFiles = Immutable.List(files);

    const updatedInteraction = interaction.set('files', nextFiles);

    actions.updateInteraction(updatedInteraction);
  };

  const saveDisabled = (): boolean => {
    const submittalDocs = getSubmittalDocs();

    return interaction.files.count() === 0 && submittalDocs.count() === 0;
  };

  const openViewSubmittalOverlay = async (
    submittal: Submittal
  ): Promise<void> => {
    const viewSubInteraction = new ViewSubmittal({
      submittal,
    });

    await actions.clearInteractions();

    actions.createInteraction(viewSubInteraction);
  };

  const handleCancelClick = async (): Promise<void> => {
    openViewSubmittalOverlay(interaction.submittal);
  };

  const handleRespondSubmittal = async (): Promise<void> => {
    const { submittal } = interaction;

    let newState = appState;
    try {
      setUpdatingSubmittal(true);
      newState = await actions.respondToSubmittal(submittal, interaction.files);
    } catch (err) {
      console.error('Unable to respond to submittal', err);
      toast.error(
        translate('toastText:submittalRespondError.message'),
        translate('toastText:submittalRespondError.title')
      );
    } finally {
      setUpdatingSubmittal(false);
      const { submittals } = newState;
      const updatedSubmittal = submittals.get(submittal.id, submittal);
      await openViewSubmittalOverlay(updatedSubmittal);
    }
  };

  const handleRemoveClick = async (): Promise<void> => {
    const nextFiles = Immutable.List();

    const updatedInteraction = interaction.set('files', nextFiles);

    actions.updateInteraction(updatedInteraction);
  };

  const getSubmittalDocs = (): Immutable.List<Doc> => {
    const { documents } = appState;
    const attachmentSet = nextSubmittal.attachments.toSet();

    const submittalDocs = documents
      .filter((d) => attachmentSet.has(d.documentid))
      .valueSeq()
      .toList();

    return submittalDocs;
  };

  const categoryType: `${string}:${string}` =
    `submittalsPage:categories.${nextSubmittal.category.kind}` as `${string}:${string}`;

  return (
    <OverlayStdContainer
      id="resp-to-sub-overlay"
      style={{ backgroundColor: 'var(--white-2)' }}
    >
      {updatingSubmittal ? (
        <LoadingOverlay text={translate('submittalsPage:uploadingPDF')} />
      ) : null}
      <OverlayHeader
        id="resp-to-sub-overlay-header"
        title={`${translate('submittalsPage:submittalNumber')}: ${
          nextSubmittal.specsectionnumber
        }-${nextSubmittal.sectioncount.toString().padStart(3, '0')}`}
        onCancelClick={handleCloseOverlay}
      />
      <div className="column justify-between os grow-1">
        <div className="mx4 my2">
          <OverlaySection label={translate('formLabels:response')}>
            <div className="my2">
              <SubmittalAttachmentButtons
                id="resp-to-sub-overlay-attach-btn"
                submittal={nextSubmittal}
                files={interaction.files}
                attachButtonTitle={translate('buttons:attach')}
                onFileSelect={handleAddFile}
                onRemoveClick={handleRemoveClick}
              />
            </div>
          </OverlaySection>
          <OverlaySection label={translate('submittalsPage:generalDetails')}>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:submittalNumberFull')}:`}
              />
              <div className="light font-size-5 color-gray-7">
                {nextSubmittal.specsectionnumber}
              </div>
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:specSection')}:`} />
              <div className="light font-size-5 color-gray-7 min-hs1">
                {nextSubmittal.specsectionname}
              </div>
            </div>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:submittalCategory')}:`}
              />
              <div className="light font-size-5 color-gray-7">
                {translate(categoryType)}
              </div>
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:description')}:`} />
              <div className="light font-size-5 color-gray-7">
                {nextSubmittal.description}
              </div>
            </div>
            <div>
              <Label
                label={`${translate('submittalsPage:responsibleCompany')}:`}
              />
              <div className="light font-size-5 color-gray-7">
                {nextSubmittal.responsiblecompany}
              </div>
            </div>
          </OverlaySection>
        </div>
      </div>
      <OverlayFooter
        id="resp-to-sub-overlay-footer"
        confirmText={translate('buttons:send')}
        onCancelClick={handleCancelClick}
        onConfirmClick={handleRespondSubmittal}
        confirmDisabled={saveDisabled()}
      />
    </OverlayStdContainer>
  );
};

export default withAppStore<Props>(RespondToSubmittalOverlay);
